import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import React from 'react';

interface FormFieldWrapperProps {
  label: string;
  name: string;
  required?: boolean;
  tooltip?: string;
  children: React.ReactNode;
  error?: string;
  className?: string;
  /**
   * Set to true for checkbox groups, radio groups, or any field where the label
   * shouldn't have a 'for' attribute (because it labels a group, not a single input)
   */
  isGroupLabel?: boolean;
}

export function FormFieldWrapper({
  label,
  required = false,
  tooltip,
  children,
  error,
  className,
  isGroupLabel = false,
}: FormFieldWrapperProps) {
  // For group labels (checkbox/radio groups), use a plain Label without htmlFor
  // For single inputs, use FormLabel which automatically connects to the input via htmlFor
  const LabelComponent = isGroupLabel ? Label : FormLabel;

  return (
    <FormItem className={className}>
      <div className='flex items-center gap-2'>
        <LabelComponent className='text-sm font-medium'>
          {label}
          {required && (
            <span className='text-destructive ml-1' aria-label='required field'>
              *
            </span>
          )}
        </LabelComponent>
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='p-1 h-6 w-6 text-muted-foreground hover:text-foreground'
                  aria-label={`Information about ${label}`}
                >
                  <Info className='h-4 w-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='top' className='max-w-xs text-sm'>
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <FormControl>{children}</FormControl>
      {error && (
        <FormMessage className='text-sm text-destructive' role='alert'>
          {error}
        </FormMessage>
      )}
    </FormItem>
  );
}

export default FormFieldWrapper;
